import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { OperatorResponse } from 'src/apis/capacity-management/types';
import { useCapacityManagement } from 'src/apis/capacity-management/use-capacity-management';
import { isServerError, useAxios } from 'src/hooks/use-axios';

type HookConfig = {
  operatorUuid: string;
};

type RequestOptions = {
  url: string;
};

export function useOperatorByTso({ operatorUuid }: HookConfig) {
  const capacityManagement = useCapacityManagement();
  const operatorLink = capacityManagement?._links?.operator;

  const operator = useAxios(
    (axios, baseConfig, { url }: RequestOptions) =>
      axios.request<OperatorResponse>({
        ...baseConfig,
        url,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        if (isServerError(error, 403)) {
          // operator is not subscribed to cama
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (!operatorLink) return;

    const url = urlTemplate
      .parse(operatorLink.href)
      .expand({ operatorId: operatorUuid });

    operator.execute({ url });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorUuid]);

  // either carma itself or the operator link aren't rolled out on this stage
  // (or we're authenticated as an operator)
  // in any case, we aren't interested in the request
  if (!operatorLink) return;

  return operator;
}

export type OperatorRequest = ReturnType<typeof useOperatorByTso>;
